import awpURL from 'omt:./awp.js';
import { setSelectedDevice, subscribeSelectedDeviceMuted, subscribeSelectedDeviceSelection } from './stores.js';
import { Devices } from './Devices.js';
import { BuildSettings, OutputConfig } from './OutputConfig.js';
import { Logger } from './Logger.js';

/*
 Output-latency settle detection (see _pollOutputLatency). A route change makes the browser
 re-open the platform output stream, and outputLatency is only trustworthy once the new stream
 is running -- these are the numbers that decide when we believe it.
*/
const AHL_POLL_MS          = 100;   // -- sample spacing while a route change settles
const AHL_SETTLE_GRACE_MS  = 400;   // -- ignore readings this soon after a trigger: still the old stream
const AHL_SETTLE_SAMPLES   = 4;     // -- consecutive equal readings that count as settled
const AHL_SETTLE_TIMEOUT_MS = 6000; // -- stop waiting for it to hold still; report the last valid read
const AHL_EPSILON_MS       = 0.5;   // -- below this, two readings are the same number
const AHL_HEARTBEAT_MS     = 2000;  // -- re-read + re-send period (see startLatencyHeartbeat)
const AHL_DRIFT_MS         = 5;     // -- the heartbeat ignores drift under this: Bluetooth figures wobble by a few ms
const GLITCH_REPORT_MS     = 10000; // -- output glitches are reported at most this often (see _sampleOutputGlitches)

class LevelAnalyzer {

    core = [];
    channels = 0;
    splitter = null;
    source_output_num = 0;
    levels = new Float32Array(0);

    constructor(ac, channels, source_node, source_output_num){
        
        this.source_output_num = source_output_num;        
        this.channels = channels;
        this.levels = new Float32Array(channels);
        this.splitter = ac.createChannelSplitter(channels);
        source_node.connect(this.splitter, source_output_num);

        for (let i = 0; i < channels; i++){
            
            const a = ac.createAnalyser();
            a.fftSize = 1024;
            a.smoothingTimeConstant = 0.8;
            const data = new Float32Array(a.frequencyBinCount);
            this.splitter.connect(a, i);
            this.core.push({a: a, data: data});
        }    
    }

    calculateChannelLevel(channel){
        if (channel < 0 || channel >= this.channels || this.channels !== this.core.length)
            return 0;

        const c = this.core[channel];
        const data = c.data;
        let rmsValue = 0;

        c.a.getFloatTimeDomainData(data);

        for (let i = 0; i < data.length; i++) {
            const sample = data[i];
            rmsValue += sample * sample;
        }

        const rms = Math.sqrt(rmsValue / data.length);
        return Math.max(0, Math.min(1, (20 * Math.log10(rms) + 60) / 60));
    }

    getChannelLevel(channel){
        return this.calculateChannelLevel(channel);
    }

    getLevels(out = this.levels){

        if (this.channels !== this.core.length)
            return null;

        for (let i = 0; i < this.channels; i++) {
            out[i] = this.calculateChannelLevel(i);
        }

        return out;
    }

}

const ZERO_1 = new Float32Array(1);
const ZERO_2 = new Float32Array(2);
const ZERO_4 = new Float32Array(4);

// -- duration of the first-Join fade-up on the stream monitor
const STREAM_FADE_SEC = 0.3;

function zeroLevels(out, fallback){
    if (!out)
        return fallback;
    out.fill(0);
    return out;
}


export const Mixer = {

    audioContext: null,
    awp: null,

    // -- set by MediaLifecycle; fired on every AudioContext state transition
    // -- ("running" | "suspended" | "interrupted"). This is how we catch iOS
    // -- foreground interruptions (calls, Siri, BT profile flips) that visibility misses.
    onStateChange: null,

    stream_las: new Map(),
    conf_las: new Map(),
    
    awp_gain: null,
    _stream_unlocked: false, // -- false until first Join; keeps the stream silent pre-join
    _stream_muted: false,    // -- mirrors the stream.muted store; read when unlockStream() ramps
    _unsub_stream_mute: null,
    _unsub_mic_selection: null,
    awp_output_msd: null,
    ios_output_el: null,
    conf_gain: null,
    conf_la: null,

    audio_hardware_latency: 25,
    latency_hint: "playback",       // -- what init() asked the context for (BuildSettings.ANDROID_LATENCY_HINT on Android)
    _glitch_base: null,             // -- playbackStats at the last glitch report: {events, ms, t}
    ahl_callback: null,
    _ahl_token: 0,
    _ahl_polling: false,            // -- a settle poll is in flight; the heartbeat stands down
    _ahl_heartbeat: null,
    _ahl_visibility_bound: false,
    _ahl_saw_output_latency: false, // -- this context has reported a non-zero outputLatency at least once
    _recovering_output: false, // -- guards _recoverFromOutputDeviceLoss against overlapping/looping

    use_expanded_io: false,

    /*
     iOS-only: hidden HTMLAudioElement used to route AudioContext output through the
     media playback path instead of AudioContext.destination when enabled in BuildSettings.
     Call this from a user gesture on iOS; if Mixer.init() already ran, this attaches it.
    */
    prepareIosOutputElement(){

        if (!BuildSettings.IOS_USE_AUDIO_ELEMENT_OUTPUT || !OutputConfig.session.browser?.isIOS)
            return;

        if (this.ios_output_el)
            return;

        const el = document.createElement('audio');
        el.autoplay = true;
        el.playsInline = true;
        el.setAttribute('playsinline', '');
        el.setAttribute('webkit-playsinline', '');
        el.setAttribute('x-webkit-airplay', 'deny');
        el.style.display = 'none';
        document.body.appendChild(el);

        el.play().catch(() => { /* expected before a source is attached */ });

        this.ios_output_el = el;
        this._connectMainOutput();
    },

    /*
     AWP features 5 outputs
     - 1 2ch output representing the mix sent to the device. This is also metered for peers with expanded_io
     - 4 4ch outputs representing talents. These are only for metering
    */

    async init(ahl_callback){

        // -- Init stream branch
        this.latency_hint = OutputConfig.session.browser?.isAndroid ? BuildSettings.ANDROID_LATENCY_HINT : "playback";
        this.audioContext = new AudioContext({latencyHint: this.latency_hint, sampleRate: 48000});
        
        this.audioContext.onstatechange = () => {
            //console.log("[PL-Mixer] AudioContext state:", this.audioContext.state);
            this.onStateChange && this.onStateChange(this.audioContext.state);
        };
        
        this.audioContext.onerror = (e) => {
            console.error("[PL-Mixer] Main Audio Context Error: ", e);
            this._recoverFromOutputDeviceLoss();
        }

        await this.audioContext.audioWorklet.addModule(awpURL);
        this.awp = new AudioWorkletNode(this.audioContext, 'awp', {numberOfInputs: [1], numberOfOutputs: [5], outputChannelCount: [2, 4, 4, 4, 4]});
        
        // -- Stream Metering --
        // -- The first 2 ch output of awp measures the summed stream
        this.stream_las.set("sum", {
            node: null,
            la: new LevelAnalyzer(this.audioContext, 2, this.awp, 0)
        })
        
        // -- Stream Muting + Output
        this.awp_gain = this.audioContext.createGain();
        this.awp.connect(this.awp_gain, 0, 0);
        // -- start silent: the app connects to streams before the user joins, so the monitor
        // -- stays muted until the first Join opens the gate (see unlockStream/setStreamMute).
        this.awp_gain.gain.setValueAtTime(0, this.audioContext.currentTime);
        this._connectMainOutput();

        // -- Keep the monitor gain in sync with the stream.muted store. The callback fires
        // -- immediately with the current value (so eng's muted-on-join default is applied no
        // -- matter the init order) and again on every toolbar toggle.
        this._unsub_stream_mute && this._unsub_stream_mute();
        this._unsub_stream_mute = subscribeSelectedDeviceMuted("stream", muted => this.setStreamMute(muted));

        // -- a mic change can move the whole audio route (a BT headset mic flips the output
        // -- with it), so re-read the hardware latency whenever the selected mic changes.
        // -- Output-device changes reassess on their own inside _setAudioContextOutputDevice,
        // -- and a raw devicechange event reassesses from App's listener.
        this._unsub_mic_selection && this._unsub_mic_selection();
        this._unsub_mic_selection = subscribeSelectedDeviceSelection("microphone", () => this.reassessOutputLatency("mic-selection"));

        // -- Init Conference Branch
        this.conf_gain = this.audioContext.createGain();
        this.conf_la = new LevelAnalyzer(this.audioContext, 1, this.conf_gain, 0);

        this.ahl_callback = ahl_callback;
        this.startLatencyHeartbeat();

        return this.awp.port;

    },

    _connectMainOutput(){

        if (!this.audioContext || !this.awp_gain)
            return;

        this.awp_gain.disconnect();

        if (BuildSettings.IOS_USE_AUDIO_ELEMENT_OUTPUT && OutputConfig.session.browser?.isIOS && this.ios_output_el){
            if (!this.awp_output_msd)
                this.awp_output_msd = this.audioContext.createMediaStreamDestination();

            this.awp_gain.connect(this.awp_output_msd);
            if (this.ios_output_el.srcObject !== this.awp_output_msd.stream)
                this.ios_output_el.srcObject = this.awp_output_msd.stream;

            this.ios_output_el.play().catch(e => {
                console.warn("[PL-Mixer] iOS output <audio> play failed: ", e);
            });
        }
        else {
            this.awp_gain.connect(this.audioContext.destination);
        }
    },

    // -- maybe we need this in a try/catch
    async resume(){

        if (!this.audioContext)
            return false;

        if (this.audioContext.state === "suspended" || this.audioContext.state === "interrupted"){
            try {
                await this.audioContext.resume();
            } catch(e) {
                console.warn("[PL-Mixer] AudioContext resume failed: ", e);
                return false;
            }
        }

        if (this.audioContext.state !== "running")
            return false;

        if (BuildSettings.IOS_USE_AUDIO_ELEMENT_OUTPUT && this.ios_output_el?.paused){
            this.ios_output_el.play().catch(e => {
                console.warn("[PL-Mixer] iOS output <audio> resume failed: ", e);
            });
        }

        this.reassessOutputLatency("ctx-resume");

        return true;
    },

    /*
     iOS-only: nudge iOS to re-evaluate the audio output route (e.g. hand the AirPods over from
     the Mac to this device). iOS picks the route when a media element *starts* playing under a
     live user gesture -- not while our output <audio> keeps streaming silently. A continuously
     autoplaying MediaStream element therefore never re-triggers evaluation, which is why selecting
     AirPods as the mic alone doesn't switch the route until the user separately presses play.

     MUST be called synchronously from inside a user gesture (e.g. the mic <select> change handler),
     BEFORE any await -- Safari invalidates the transient activation across await boundaries. The
     pause()->play() cycle is what makes this look like a fresh play start rather than ongoing
     playback, re-activating the audio session and letting iOS grab the (BT) route.
    */
    kickIosOutputRoute(){

        if (!OutputConfig.session.browser?.isIOS)
            return;

        this.audioContext?.resume?.().catch(() => {});

        const el = this.ios_output_el;
        if (el){
            try {
                el.pause();
                el.play().catch(e => console.warn("[PL-Mixer] iOS route-kick play failed: ", e));
            } catch(e) {
                console.warn("[PL-Mixer] iOS route-kick failed: ", e);
            }
        }
    },

    async suspend(){

        if (!this.audioContext || this.audioContext.state !== "running")
            return;

        try {
            await this.audioContext.suspend();
        } catch(e) {
            console.warn("[PL-Mixer] AudioContext suspend failed: ", e);
        }
    },

    /*
     The pinned output sink disappeared from the system (e.g. AirPods handed off to an iOS device).
     Chrome faults the renderer (onerror) and suspends the context, but the sink still points at the
     now-dead device -- resuming alone reconnects to nothing (outputLatency keeps reporting the stale
     ~200ms AirPods figure). So re-point at the system default (a guaranteed-live sink) FIRST, then
     resume, then reassess latency now that we're actually rendering on the new route.

     Order matters: setSinkId before resume. Resuming onto a dead sink is what fails today; switching
     the sink to a live device first is what lets resume() actually bring audio back.
    */
    async _recoverFromOutputDeviceLoss(){

        if (!this.audioContext || this._recovering_output)
            return false;

        this._recovering_output = true;

        try {
            // -- setSinkId is desktop-Chrome only; iOS/Safari follow the system default automatically
            // -- (no pinned sink to clear), so there we just resume.
            if (typeof this.audioContext.setSinkId === "function"){
                try {
                    await this.audioContext.setSinkId(''); // -- '' == system default
                } catch(err) {
                    console.warn("[PL-Mixer] setSinkId(default) during recovery failed: ", err);
                }
            }

            const ok = await this.resume();

            // -- only reassess once we're actually running on the new (default) sink, otherwise we'd
            // -- poll the stale latency of the device that just vanished.
            if (ok)
                this.reassessOutputLatency("device-loss-recovery");

            console.log("[PL-Mixer] Output device loss recovery", ok ? "succeeded" : "did not restore running state");
            return ok;

        } finally {
            this._recovering_output = false;
        }
    },


    // -- Mixer Getters and Setters -- //

    getStreamSumLevel(out = null){
        if (!this.stream_las.has("sum"))
            return zeroLevels(out, ZERO_2);
        return this.stream_las.get("sum").la.getLevels(out ?? undefined);
    },

    // -- Not Used
    getConfSumLevel(out = null){
        if (!this.conf_la) 
            return zeroLevels(out, ZERO_1);
        return this.conf_la.getLevels(out ?? undefined);
    },

    getConfInputLevel(id, out = null){
        if (!id || !this.conf_las.has(id))
            return zeroLevels(out, ZERO_1);
        return this.conf_las.get(id).la.getLevels(out ?? undefined);
    },

    getRemoteTalentLevel(id, out = null){
        if (!id || !this.stream_las.has(id))
            return zeroLevels(out, ZERO_4);
        return this.stream_las.get(id).la.getLevels(out ?? undefined);
    },

    getRemoteTalentChannelLevel(id, channelNum){
        if (!id || !this.stream_las.has(id))
            return 0;
        return this.stream_las.get(id).la.getChannelLevel(channelNum);
    },

    setStreamMute(muted){
        this._stream_muted = muted;
        if (!this.awp_gain) return;
        // -- Pre-join the monitor stays silent regardless of the store; the first Join (unlockStream)
        // -- opens the gate, after which the awp gain simply follows the user's mute choice.
        const gain = (this._stream_unlocked && !muted) ? 1 : 0;
        this.awp_gain.gain.setValueAtTime(gain, this.audioContext.currentTime);
    },

    // -- Called once, on the user's first Join. Fades the stream up from its pre-join silence to
    // -- the mute-appropriate level. Idempotent: later Joins (after returning to the start panel)
    // -- are no-ops, so the awp gain keeps honoring whatever mute choice is in effect.
    unlockStream(){
        if (this._stream_unlocked) return;
        this._stream_unlocked = true;
        if (!this.awp_gain) return; //maybe we need to assert this
        const target = this._stream_muted ? 0 : 1;
        const t = this.audioContext.currentTime;
        this.awp_gain.gain.cancelScheduledValues(t);
        this.awp_gain.gain.setValueAtTime(this.awp_gain.gain.value, t);
        this.awp_gain.gain.linearRampToValueAtTime(target, t + STREAM_FADE_SEC);
    },

    // -- -- -- -- -- -- -- //


    //
    async setOutputDevice(type, label, self_type, _id = null, use_expanded_io = false){
        
        try {
            
            const id = _id ?? Devices.getDeviceByLabel(type, label)?.deviceId;
            console.log("[PL-Mixer] Setting Output Device: ", type, id, type, use_expanded_io)
            let ok = false;

            if (self_type === "client" && type === "speaker"){
                await this._setAudioContextOutputDevice(id);
                ok = true;
            }

            else if (self_type === "eng"){
            
                if (type === "stream")
                    await this._setAudioContextOutputDevice(id);
                
                ok = true;
            }

            else if (self_type === "talent"){
                
                if (use_expanded_io){
                    type === "stream" && await this._setAudioContextOutputDevice(id);
                    ok = true;
                }

                else if (type === "speaker"){
                    await this._setAudioContextOutputDevice(id);
                    ok = true;
                }

            }

            if (ok) setSelectedDevice(type, {label: label, id: id});
            return ok;

        } catch(e) {
            console.error(e);
            return false;
        }
    
    },

    async _setAudioContextOutputDevice(id){

        console.assert(this.audioContext);
        if (!id) id = 'default';
        if (id === 'default') await this.audioContext.setSinkId('');
        else await this.audioContext.setSinkId(id);

        this.reassessOutputLatency("sink-change");
    },


    // -- Output Latency -- //

    /*
     Re-read the audio output latency, settle it, and publish it.

     Triggered whenever the output route may have shifted: output device change, mic change
     (a BT mic can move the whole audio route), a devicechange event, a context resume, or the
     heartbeat below noticing the number moved on its own.
    */
    async reassessOutputLatency(reason = "event"){

        if (!this.audioContext)
            return;

        const token = ++this._ahl_token;
        this._ahl_polling = true;

        try {
            const settled = await this._pollOutputLatency(() => token === this._ahl_token);

            // -- superseded by a newer reassess, or nothing valid to report
            if (token !== this._ahl_token || !Number.isFinite(settled))
                return;

            this._publishOutputLatency(settled, reason);

        } finally {
            if (token === this._ahl_token)
                this._ahl_polling = false;
        }
    },

    /*
     The one place that owns the cached value and pushes it downstream. The push is
     UNCONDITIONAL: the AWP dedupes on its own copy, so re-sending an unchanged number costs a
     postMessage and repairs anything that got dropped on the way down (a value measured before
     the decoder thread existed, say). Only a real change is worth a log line.
    */
    _publishOutputLatency(ms, reason){

        const moved = Math.abs(ms - this.audio_hardware_latency);
        if (reason === "heartbeat" ? moved >= AHL_DRIFT_MS : moved > AHL_EPSILON_MS){
            // -- with the parts behind it: base_ms is the callback size the hint got us
            const ac = this.audioContext;
            Logger.log("Devices", "info", "Setting Audio Context Latency", {
                latency: ms, reason: reason, hint: this.latency_hint,
                base_ms: ac ? Math.round(ac.baseLatency * 1e5) / 100 : null,
                output_ms: ac ? Math.round(ac.outputLatency * 1e5) / 100 : null,
            });
            console.log(`[PL-Mixer] Output Latency: ${ms}ms (was ${this.audio_hardware_latency}ms -- ${reason})`);
            this.audio_hardware_latency = ms;
        }

        this.ahl_callback && this.ahl_callback(this.audio_hardware_latency);
    },

    _getTotalOutputLatency(){

        // -- a suspended/closed context reports whatever it last had; that number describes a
        // -- route we are no longer rendering on
        if (!this.audioContext || this.audioContext.state !== "running")
            return NaN;

        const out = this.audioContext.outputLatency;

        // -- Chrome reads 0 between tearing down the old platform stream and the first callback
        // -- of the new one. An engine that always reports 0 is a different case, so calibrate on
        // -- this context: once it has produced a real figure, a later 0 is a transition artifact.
        if (out > 0)
            this._ahl_saw_output_latency = true;
        else if (this._ahl_saw_output_latency)
            return NaN;

        const base = this.audioContext.baseLatency;

        // -- Chromium's outputLatency already spans the baseLatency buffer, which renders as one
        // -- burst: its first quantum is heard outputLatency after process(), its last a burst
        // -- later. The AWP stamps every quantum, so take the average wait.
        if (OutputConfig.session.browser?.engine === "chromium")
            return (out + (base - 128 / this.audioContext.sampleRate) / 2) * 1000;

        return (base + out) * 1000;
    },

    /*
     Wait for the output latency to SETTLE, and return it.

     A route change -- our own setSinkId, or macOS moving the system default out from under a
     pinned sink -- makes Chrome re-open the platform output stream, and outputLatency walks
     through intermediate readings on the way: the old device's figure, 0 while there is no
     stream, sometimes a first estimate before the real buffer size is negotiated. Believing the
     first reading that moved latches whichever of those we happened to sample, and believing a
     reading taken before the re-open even starts latches the device we just left. So: ignore
     everything inside the grace window, then require a value to repeat before accepting it.

     Returns NaN if superseded (isCurrent goes false) or if nothing valid was ever read.
    */
    async _pollOutputLatency(isCurrent){

        const start = performance.now();

        let candidate = NaN;
        let hits = 0;
        let last_valid = NaN;

        while (performance.now() - start < AHL_SETTLE_TIMEOUT_MS){

            await new Promise(r => setTimeout(r, AHL_POLL_MS));

            if (!isCurrent())
                return NaN;

            const current = this._getTotalOutputLatency();

            // -- mid-transition: no stream to measure. Keep what we have and keep waiting.
            if (!Number.isFinite(current)){
                candidate = NaN;
                hits = 0;
                continue;
            }

            last_valid = current;

            if (performance.now() - start < AHL_SETTLE_GRACE_MS)
                continue;

            hits = (Number.isFinite(candidate) && Math.abs(current - candidate) <= AHL_EPSILON_MS) ? hits + 1 : 1;
            candidate = current;

            if (hits >= AHL_SETTLE_SAMPLES)
                return current;
        }

        // -- never held still (or timers are being throttled hard enough that we only got a
        // -- couple of samples): the last valid reading still beats the pre-change one, and the
        // -- heartbeat re-checks within AHL_HEARTBEAT_MS.
        return last_valid;
    },

    /*
     Every path that sets the output latency is edge-triggered, and every edge can be missed: a
     route change the browser never reports, a reading taken mid-re-open, a push that lands
     before the decoder thread exists. Nothing downstream re-asks, so a single miss leaves the
     AWP on a stale figure -- and that figure is the whole A/V sync offset.

     So re-read it on a timer and re-send it every tick. The AWP ignores an unchanged value (no
     preroll), which makes the steady-state cost one postMessage per tick.
    */
    startLatencyHeartbeat(){

        if (this._ahl_heartbeat === null)
            this._ahl_heartbeat = setInterval(() => this._heartbeatTick(), AHL_HEARTBEAT_MS);

        // -- timers in a hidden tab are throttled to 1/s, and to 1/min once it has been hidden
        // -- for 5 minutes, so a device changed while we were in the background can go a long
        // -- time unnoticed. Coming back is exactly when it matters: re-check then, not on the
        // -- next tick.
        if (!this._ahl_visibility_bound){
            this._ahl_visibility_bound = true;
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible")
                    this.reassessOutputLatency("page-visible");
            });
        }
    },

    _heartbeatTick(){

        this._sampleOutputGlitches();

        // -- a settle poll is already running; it publishes when it lands
        if (this._ahl_polling)
            return;

        const current = this._getTotalOutputLatency();

        // -- not running, or mid-transition: leave the last good value alone and try next tick
        if (!Number.isFinite(current))
            return;

        // -- it moved without anyone telling us (or a reading we took mid-re-open has since
        // -- settled). Re-measure properly rather than publishing a raw sample.
        if (Math.abs(current - this.audio_hardware_latency) >= AHL_DRIFT_MS){
            this.reassessOutputLatency("heartbeat");
            return;
        }

        // -- unchanged: re-send, so anything dropped downstream gets repaired
        this.ahl_callback && this.ahl_callback(this.audio_hardware_latency);
    },

    /*
     Output glitches: the device wanted audio the renderer hadn't produced in time (Chromium's
     playbackStats; other engines have none). Smaller callbacks ("interactive") risk more of
     these on a slow phone, so report the growth, at most once per GLITCH_REPORT_MS.
    */
    _sampleOutputGlitches(){

        const s = this.audioContext?.playbackStats;
        if (!s) return;

        const now = {events: s.underrunEvents, ms: s.underrunDuration * 1000, t: performance.now()};
        const base = this._glitch_base;
        if (base && now.t - base.t < GLITCH_REPORT_MS) return;
        this._glitch_base = now;

        if (base && now.events > base.events)
            Logger.log("Devices", "warn", "Audio Output Glitches", {
                events: now.events - base.events,
                glitch_ms: Math.round(now.ms - base.ms),
                window_s: Math.round((now.t - base.t) / 1000),
                hint: this.latency_hint,
            });
    },


    addStreamSource(id, source_output_num){
        
        console.assert(this.audioContext);
        this.stream_las.set(id, {
            node: null,
            la: new LevelAnalyzer(this.audioContext, 4, this.awp, source_output_num)
        })
        console.log("[PL-Mixer] Added Talent/Eng Stream Source: ", id, source_output_num);
    },

    removeStreamSource(id){
        
        if (!id || !this.stream_las.has(id))
            return;
        console.log("[PL-Mixer] Removed Talent/Eng Stream Source: ", id, this.stream_las.get(id).la.source_output_num);
        this.stream_las.delete(id);
    },

    // -- id can be peer.id or "self"
    addConfSource(stream, id){
        
        console.assert(this.audioContext);
        console.assert(this.conf_gain);

        this.removeConfSource(id);

        const conf_node = this.audioContext.createMediaStreamSource(stream);
        const channels = conf_node.channelCount;
        const la = new LevelAnalyzer(this.audioContext, channels, conf_node, 0);

        // -- we connect all remote tracks to a single gain node, in case we want to have a confrence level, or conference meter
        // -- its mix minus self. We can also get into individual levels if we want as well (this should be sticky and apply to everyone?)
        if (id !== "self") conf_node.connect(this.conf_gain);

        this.conf_las.set(id, {
            node: conf_node,
            la: la
        })

        console.log("[PL-Mixer] Added Conference Node and Level Analyzer: ", id);

    },

    removeConfSource(id){
        
        if (!id || !this.conf_las.has(id)) return;

        const conf = this.conf_las.get(id); 
        conf.node?.disconnect();
        this.conf_las.delete(id);
        console.log("[PL-Mixer] Removed Conference Node and Level Analyzer: ", id);

    },

}
